import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Users } from "lucide-react";
import { Logo } from "../components/Logo";
import { BrandLine } from "../components/Brand";
import { useApi } from "../hooks/useApi";
import { web, webUrl } from "../lib/api";
import { Loader, Panel, PillTabs, StatTile, fmt, pct, shortDate, positionsOf, CaptainBadge } from "../components/app/kit";
import { TradingCard } from "../components/app/TradingCard";
import { FanFollow } from "../components/app/FanFollow";
import { FanLineup } from "../components/app/FanLineup";
import { PoweredByLink } from "../components/app/PoweredBy";

const COLS = [["ppg", "PPG"], ["rpg", "RPG"], ["apg", "APG"], ["spg", "SPG"], ["bpg", "BPG"], ["fg_pct", "FG%"], ["fg3_pct", "3P%"], ["ft_pct", "FT%"], ["ts_pct", "TS%"]];

const PlayerCard = ({ p, team }) => (
  <div data-testid={`fan-player-${p.id}`} className="flex flex-col items-center gap-3 rounded-2xl border border-[#1E293B] bg-[#0F172A] p-4 text-center">
    <TradingCard p={p} teamName={team} photo={webUrl(p.photo_url)} season={p.season} perGame={p.per_game} publicView />
    <div>
      <p className="flex items-center justify-center gap-2 font-semibold text-white">#{p.jersey ?? "–"} {p.name}{p.captain && <CaptainBadge testid={`fan-captain-${p.id}`} />}</p>
      <p className="text-sm text-slate-400">{positionsOf(p)} · Class of {p.grad_year ?? "—"}</p>
      <p className="mt-1 font-mono-gd text-sm text-[#39FF14]">{p.season?.gp ? `${fmt(p.season?.ppg)} PPG · ${fmt(p.season?.rpg)} RPG · ${fmt(p.season?.apg)} APG` : "No games yet"}</p>
    </div>
  </div>
);

const StatsTable = ({ players }) => {
  const [sort, setSort] = useState("ppg");
  const rows = useMemo(() => [...players].sort((a, b) => (b.season?.[sort] ?? -1) - (a.season?.[sort] ?? -1)), [players, sort]);
  return (
    <div className="overflow-x-auto rounded-2xl border border-[#1E293B]" data-testid="fan-stats-table">
      <table className="w-full min-w-[720px] text-left text-sm">
        <thead className="bg-[#162032] font-mono-gd text-[11px] uppercase tracking-wider text-slate-400">
          <tr>
            <th className="px-4 py-3">Player</th><th className="px-3 py-3">GP</th>
            {COLS.map(([k, l]) => (
              <th key={k} className="px-3 py-3">
                <button data-testid={`fan-sort-${k}`} onClick={() => setSort(k)} className={sort === k ? "text-[#39FF14]" : "hover:text-white"}>{l}</button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.id} className="border-t border-[#1E293B] bg-[#0F172A]">
              <td className="px-4 py-3 font-semibold text-white">#{p.jersey ?? "–"} {p.name}</td>
              <td className="px-3 py-3 font-mono-gd text-slate-300">{p.season?.gp ?? 0}</td>
              {COLS.map(([k]) => <td key={k} className="px-3 py-3 font-mono-gd text-slate-200">{k.endsWith("pct") ? pct(p.season?.[k]) : fmt(p.season?.[k])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Schedule = ({ games }) => (
  <Panel data-testid="fan-schedule">
    {games.length ? games.map((g) => (
      <div key={g.id} className="flex items-center justify-between border-t border-[#1E293B] py-3 first:border-t-0">
        <div><p className="font-semibold text-white">vs {g.opponent ?? "TBA"}</p><p className="text-sm text-slate-400">{shortDate(g.date)}</p></div>
        {g.final
          ? <p className="font-mono-gd text-lg"><span className={g.us > g.them ? "text-[#39FF14]" : "text-red-300"}>{g.us > g.them ? "W" : "L"}</span> <span className="text-white">{`${g.us}-${g.them}`}</span></p>
          : <span className="font-mono-gd text-xs uppercase tracking-wider text-slate-400">Upcoming</span>}
      </div>
    )) : <p className="text-slate-400">No games on the schedule yet.</p>}
  </Panel>
);

export default function FanTeam() {
  const { slug } = useParams();
  const { data, loading, error } = useApi(`/public/teams/${slug}`, web);
  const [tab, setTab] = useState("Players");
  const players = Array.isArray(data?.players) ? data.players : [];
  return (
    <div className="min-h-screen bg-[#0A0E1A] text-slate-100" data-testid="fan-team-page">
      <header className="border-b border-white/5">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-4 sm:px-10">
          <Logo />
          <Link to="/" data-testid="fan-home-link" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white"><ArrowLeft className="h-4 w-4" />Find another team</Link>
        </div>
      </header>
      <main className="mx-auto max-w-[1400px] px-5 py-10 sm:px-10">
        {loading && !data ? <Loader /> : error ? (
          <div data-testid="fan-team-missing" className="py-20 text-center">
            <p className="font-display text-4xl font-black uppercase text-white">Team page not found</p>
            <p className="mt-2 text-slate-400">This team page isn't public, or the link changed.</p>
          </div>
        ) : (
          <>
            <p className="font-mono-gd text-xs uppercase tracking-[0.25em] text-[#39FF14]">Fan page{data?.city ? ` · ${data.city}` : ""}</p>
            <h1 data-testid="fan-team-name" className="mt-3 font-display text-4xl font-black uppercase leading-none text-white sm:text-5xl lg:text-6xl">{data?.team_name}</h1>
            <BrandLine className="mt-3" />
            <div className="mt-6 max-w-xl"><FanFollow slug={slug} teamName={data?.team_name} /></div>
            <div className="my-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <StatTile testid="fan-record" label="Record" value={`${data?.record?.w ?? 0}-${data?.record?.l ?? 0}`} accent />
              <StatTile label="Players" value={players.length} />
              <StatTile label="Games" value={(data?.games ?? []).length} />
              <StatTile label="Updated" value={<span className="text-base">{data?.refreshed_at ? shortDate(data.refreshed_at) : "—"}</span>} />
            </div>
            <FanLineup lineup={data?.lineup} />
            <PillTabs tabs={["Players", "Stats", "Schedule"]} value={tab} onChange={setTab} />
            {tab === "Players" && (players.length
              ? <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">{players.map((p) => <PlayerCard key={p.id} p={p} team={data?.team_name} />)}</div>
              : <p className="flex items-center gap-2 text-slate-400"><Users className="h-4 w-4" />No players listed yet.</p>)}
            {tab === "Stats" && <StatsTable players={players} />}
            {tab === "Schedule" && <Schedule games={data?.games ?? []} />}
            <p className="mt-10 text-sm text-slate-400">Fan pages show season stats and cards only. <Link to="/visibility" className="text-white underline decoration-[#39FF14]/60">Who sees what</Link></p>
          </>
        )}
      </main>
      <footer className="border-t border-white/5">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center justify-between gap-3 px-5 py-6 sm:px-10">
          <PoweredByLink />
          <p className="text-xs text-slate-400">Team stats by GAMEDAY Courtside</p>
        </div>
      </footer>
    </div>
  );
}
